/**
 * Encryption configuration for the UI
 * Manages encryption settings and environment variables
 */

export interface EncryptionConfig {
  enabled: boolean;
  secretKey: string;
  algorithm: string;
  transformation?: string;
  keyLength?: number;
  skipPaths: string[];
}

export interface BackendEncryptionConfig {
  enabled: boolean;
  keyLength: number;
  algorithm: string;
  transformation: string;
}

/**
 * Default encryption configuration - defaults to ENABLED for security
 */
const defaultConfig: EncryptionConfig = {
  enabled: true,  // Always default to enabled for security
  secretKey: 'enpropeLSecretKey2024!@#$%^&*()',
  algorithm: 'AES',
  transformation: 'AES/GCM/NoPadding',
  keyLength: 256,
  skipPaths: [
    '/health',
    '/actuator',
    '/v3/api-docs',
    '/swagger-ui',
    '/h2-console',
    '/auth/login',
    '/auth/register',
    '/config/encryption'
  ]
};

/** Dedupes concurrent/StrictMode duplicate fetches on app startup. */
let backendEncryptionConfigPromise: Promise<BackendEncryptionConfig> | null = null;

/**
 * Fetch encryption configuration from backend (cached for the session).
 */
export async function fetchBackendEncryptionConfig(): Promise<BackendEncryptionConfig> {
  if (!backendEncryptionConfigPromise) {
    backendEncryptionConfigPromise = fetchBackendEncryptionConfigOnce();
  }
  return backendEncryptionConfigPromise;
}

async function fetchBackendEncryptionConfigOnce(): Promise<BackendEncryptionConfig> {
  try {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
    const url = `${apiBaseUrl}/config/encryption`;
    
    console.log('🔍 Fetching backend encryption config from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    console.log('📡 Backend config response status:', response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`Failed to fetch encryption config: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Backend encryption config received:', result.data);
    return result.data;
  } catch (error) {
    console.warn('❌ Failed to fetch encryption config from backend, defaulting to DISABLED:', error);
    // Default to DISABLED when backend config fails for safety
    return {
      enabled: false,
      keyLength: defaultConfig.keyLength || 256,
      algorithm: defaultConfig.algorithm,
      transformation: defaultConfig.transformation || 'AES/GCM/NoPadding'
    };
  }
}

/**
 * Get encryption configuration from environment variables and backend
 */
export async function getEncryptionConfig(): Promise<EncryptionConfig> {
  // First, try to get config from backend
  const backendConfig = await fetchBackendEncryptionConfig();
  
  return {
    enabled: backendConfig.enabled,
    secretKey: process.env.REACT_APP_ENCRYPTION_KEY || defaultConfig.secretKey,
    algorithm: backendConfig.algorithm || process.env.REACT_APP_ENCRYPTION_ALGORITHM || defaultConfig.algorithm,
    transformation: backendConfig.transformation || defaultConfig.transformation,
    keyLength: backendConfig.keyLength || defaultConfig.keyLength,
    skipPaths: [
      ...defaultConfig.skipPaths,
      ...(process.env.REACT_APP_ENCRYPTION_SKIP_PATHS?.split(',') || [])
    ]
  };
}

/**
 * Validate encryption configuration
 */
export function validateEncryptionConfig(config: EncryptionConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.secretKey || config.secretKey.length < 16) {
    errors.push('Encryption key must be at least 16 characters long');
  }

  if (config.secretKey === defaultConfig.secretKey && process.env.NODE_ENV === 'production') {
    errors.push('Default encryption key should not be used in production');
  }

  if (!config.algorithm) {
    errors.push('Encryption algorithm must be specified');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get environment-specific encryption settings
 */
export function getEnvironmentEncryptionSettings(): {
  isProduction: boolean;
  isDevelopment: boolean;
  isTest: boolean;
  shouldLogEncryption: boolean;
} {
  const isProduction = process.env.NODE_ENV === 'production';
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isTest = process.env.NODE_ENV === 'test';

  return {
    isProduction,
    isDevelopment,
    isTest,
    shouldLogEncryption: !isProduction // Don't log encryption details in production
  };
}

/**
 * Initialize encryption system
 */
export async function initializeEncryption(): Promise<{ success: boolean; message: string; config: EncryptionConfig }> {
  try {
    const config = await getEncryptionConfig();
    const validation = validateEncryptionConfig(config);
    const envSettings = getEnvironmentEncryptionSettings();

    if (!validation.valid) {
      console.error('Encryption configuration validation failed:', validation.errors);
      return {
        success: false,
        message: `Encryption configuration invalid: ${validation.errors.join(', ')}`,
        config
      };
    }

    // Update the EncryptionService with the backend configuration
    try {
      const { EncryptionService } = await import('../utils/encryption');
      const encryptionService = EncryptionService.getInstance();
      encryptionService.updateConfig(config.enabled, config.secretKey);
      console.log('✅ EncryptionService updated with backend config:', { enabled: config.enabled });
    } catch (importError) {
      console.warn('⚠️ Could not update EncryptionService:', importError);
    }

    if (envSettings.shouldLogEncryption) {
      console.log('Encryption system initialized:', {
        enabled: config.enabled,
        algorithm: config.algorithm,
        transformation: config.transformation,
        keyLength: config.keyLength,
        skipPathsCount: config.skipPaths.length,
        environment: process.env.NODE_ENV
      });
    }

    return {
      success: true,
      message: 'Encryption system initialized successfully',
      config
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Failed to initialize encryption system:', errorMessage);
    
    return {
      success: false,
      message: `Failed to initialize encryption: ${errorMessage}`,
      config: defaultConfig
    };
  }
}

// Export default configuration
export default defaultConfig;
